import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
  CCard,
  CCardHeader,
  CCardBody,
  CButton,
  CBadge,
  CAlert,
  CListGroup,
  CListGroupItem,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CModalTitle,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilBell, cilCheckCircle, cilClock } from '@coreui/icons';
import { AuthUserContext } from '../views/session/context';
import config from '../utils/EnvironmentSwitcher';
import { callAI } from '../services/aiChatService';
import { ensureFhirUserIdentifier } from '../utils/fhirIdentifier';
import { buildFhirHeaders } from '../utils/fhirAuth';
import { toast } from 'react-toastify';

const DEFAULT_HEALTH_SYSTEM_MESSAGE = 'You are MiEROS, a friendly AI health companion. Analyze the user\'s health observations and provide personalized, actionable insights. Be encouraging but honest. Use their actual data numbers in your response. Keep responses concise (2-3 short paragraphs). End with a practical tip. Always remind users to consult healthcare professionals for medical decisions.';

// All AI prompts go to the mieGPT endpoint (chat.mieros.cloud/gptj). See src/services/aiChatService.js.
const callOpenAI = async (prompt, systemMessage = DEFAULT_HEALTH_SYSTEM_MESSAGE) =>
  callAI({ prompt, systemMessage });

const CircadianAIAssistant = ({ authUser, onRefresh }) => {
  // Circadian AI Assistant States
  const [tasks, setTasks] = useState([]) // Pending tasks from FHIR
  const [notificationsEnabled, setNotificationsEnabled] = useState(false)
  const [pushSubscription, setPushSubscription] = useState(null)
  const [aiProcessing, setAiProcessing] = useState(false)
  const [healthInsights, setHealthInsights] = useState([])
  const [inAppNotifications, setInAppNotifications] = useState([]) // In-app notification queue
  const [pwaInstallPrompt, setPwaInstallPrompt] = useState(null)
  const [isPwaInstalled, setIsPwaInstalled] = useState(false)
  const circadianCheckInterval = useRef(null)
  const fhirEndpoint = `${String(config.apiUrl || '').replace(/\/+$/, '')}/api/fhir`
  // fhirSystemBase is canonical — FHIR identifier system URIs are permanent labels baked into
  // stored resources and do NOT change between dev/prod environments.
  const fhirSystemBase = `${String(config.fhirSystemBase || '').replace(/\/+$/, '')}`
  
  // AI Suggestion States
  const [aiSuggestions, setAiSuggestions] = useState([]) // Dynamic AI question buttons
  const [selectedSuggestion, setSelectedSuggestion] = useState(null) // Currently selected suggestion
  const [suggestionResponse, setSuggestionResponse] = useState(null) // AI response
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false) // Loading state
  const [rawObservations, setRawObservations] = useState(null) // Store fetched observations
  const [isAudioPlaying, setIsAudioPlaying] = useState(false) // Audio playback state
  const speechSynthRef = useRef(null) // Reference to current speech utterance

  // ============= PWA FUNCTIONS =============
  
  // Check if app is running as PWA
  const checkPwaStatus = () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isInApp = window.navigator.standalone === true;
    setIsPwaInstalled(isStandalone || isInApp);
    console.log('📱 PWA Status:', { isStandalone, isInApp, installed: isStandalone || isInApp });
  };
  
  // Handle PWA install prompt
  const handlePwaInstallPrompt = () => {
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('📱 PWA install prompt available');
      e.preventDefault();
      setPwaInstallPrompt(e);
    });
    
    window.addEventListener('appinstalled', () => {
      console.log('✅ PWA installed successfully');
      setPwaInstallPrompt(null);
      setIsPwaInstalled(true);
      toast.success('App installed! You can now receive notifications even when the browser is closed.');
    });
  };
  
  // Install PWA
  const installPwa = async () => {
    if (!pwaInstallPrompt) return;
    
    try {
      const result = await pwaInstallPrompt.prompt();
      console.log('📱 PWA install prompt result:', result);
      
      if (result.outcome === 'accepted') {
        toast.success('Installing app...');
      } else {
        toast.info('Installation cancelled');
      }
      
      setPwaInstallPrompt(null);
    } catch (error) {
      console.error('❌ PWA install error:', error);
      toast.error('Failed to install app');
    }
  };

  // ============= AI SUGGESTION FUNCTIONS =============
  
  // Generate dynamic AI suggestions based on available observations
  const generateAISuggestions = useCallback((observations) => {
    if (!observations?.entry?.length) return [];
    
    const suggestions = [];
    const observationTypes = {};
    
    // Group observations by type and calculate stats
    observations.entry.forEach(entry => {
      const displayName = entry.resource?.code?.coding?.[0]?.display || entry.resource?.code?.text || 'Unknown';
      const value = entry.resource?.valueQuantity?.value;
      const unit = entry.resource?.valueQuantity?.unit || '';
      
      if (!observationTypes[displayName]) {
        observationTypes[displayName] = { values: [], unit, count: 0 };
      }
      
      if (value !== undefined) {
        observationTypes[displayName].values.push(value);
        observationTypes[displayName].count++;
      }
    });
    
    // Generate suggestions based on available observation types
    Object.entries(observationTypes).forEach(([type, data]) => {
      const avg = data.values.length > 0 
        ? (data.values.reduce((a, b) => a + b, 0) / data.values.length).toFixed(1) 
        : null;
      const min = data.values.length > 0 ? Math.min(...data.values) : null;
      const max = data.values.length > 0 ? Math.max(...data.values) : null;
      
      const typeLower = type.toLowerCase();
      
      if (typeLower.includes('sleep')) {
        suggestions.push({
          id: 'sleep-quality', icon: '😴', label: 'Am I getting enough sleep?', color: 'info',
          type, context: { avg, min, max, count: data.count, unit: data.unit },
          prompt: `Based on my sleep data (${data.count} entries, average: ${avg} ${data.unit}, range: ${min}-${max} ${data.unit}), am I getting enough sleep? What's the ideal amount and how do I compare? Give me specific recommendations.`
        });
      }
      
      if (typeLower.includes('step')) {
        suggestions.push({
          id: 'steps-goal', icon: '🚶', label: 'Am I hitting my step goals?', color: 'success',
          type, context: { avg, min, max, count: data.count, unit: data.unit },
          prompt: `My step data shows ${data.count} entries with an average of ${avg} steps/day (range: ${min?.toLocaleString()}-${max?.toLocaleString()}). Am I meeting recommended daily step goals? How can I improve?`
        });
      }
      
      if (typeLower.includes('heart') || typeLower.includes('hr')) {
        suggestions.push({
          id: 'heart-health', icon: '❤️', label: 'Is my heart rate healthy?', color: 'danger',
          type, context: { avg, min, max, count: data.count, unit: data.unit },
          prompt: `My heart rate data shows ${data.count} readings with an average of ${avg} bpm (range: ${min}-${max} bpm). Is this a healthy resting heart rate? What factors affect it?`
        });
      }
      
      if (typeLower.includes('weight')) {
        suggestions.push({
          id: 'weight-trend', icon: '⚖️', label: "What's my weight trend?", color: 'primary',
          type, context: { avg, min, max, count: data.count, unit: data.unit },
          prompt: `My weight data shows ${data.count} entries, ranging from ${min} to ${max} ${data.unit} (average: ${avg} ${data.unit}). What does this trend suggest? Is this healthy variation?`
        });
      }
      
      if (typeLower.includes('calorie')) {
        suggestions.push({
          id: 'calories-intake', icon: '🍎', label: 'How are my calories?', color: 'warning',
          type, context: { avg, min, max, count: data.count, unit: data.unit },
          prompt: `My calorie intake data shows ${data.count} entries with an average of ${avg} kcal/day (range: ${min?.toLocaleString()}-${max?.toLocaleString()} kcal). Is this appropriate for my health?`
        });
      }
      
      if (typeLower.includes('glucose') || typeLower.includes('sugar')) {
        suggestions.push({
          id: 'blood-glucose', icon: '🩸', label: "How's my blood glucose?", color: 'danger',
          type, context: { avg, min, max, count: data.count, unit: data.unit },
          prompt: `My blood glucose readings show ${data.count} entries with an average of ${avg} ${data.unit} (range: ${min}-${max} ${data.unit}). Are these levels healthy?`
        });
      }
      
      if (typeLower.includes('water') || typeLower.includes('hydration')) {
        suggestions.push({
          id: 'hydration', icon: '💧', label: 'Am I drinking enough water?', color: 'info',
          type, context: { avg, min, max, count: data.count, unit: data.unit },
          prompt: `My water intake data shows ${data.count} entries with an average of ${avg} ${data.unit}/day. Am I properly hydrated? What's the recommended daily intake?`
        });
      }
    });
    
    // Add overall health suggestion if we have enough data types
    if (Object.keys(observationTypes).length >= 3) {
      suggestions.push({
        id: 'overall-health', icon: '🏥', label: "How's my overall health?", color: 'primary',
        type: 'overall',
        context: { totalTypes: Object.keys(observationTypes).length, totalEntries: observations.entry.length },
        prompt: `Based on all my health observations (${observations.entry.length} total entries across ${Object.keys(observationTypes).length} different metrics), give me an overall health assessment and top 3 actionable recommendations.`
      });
    }
    
    // Remove duplicates and limit
    const uniqueSuggestions = suggestions.filter((s, i, self) => i === self.findIndex(t => t.id === s.id));
    return uniqueSuggestions.slice(0, 8);
  }, []);
  
  // Handle clicking on an AI suggestion
  const handleSuggestionClick = async (suggestion) => {
    setSelectedSuggestion(suggestion);
    setSuggestionResponse(null);
    setIsSuggestionLoading(true);
    
    try {
      const response = await callOpenAI(suggestion.prompt);
      setSuggestionResponse(response);
    } catch (error) {
      console.error('Error getting AI suggestion response:', error);
      toast.error('Failed to get AI insights');
      setSuggestionResponse('Sorry, I couldn\'t generate insights right now. Please try again.');
    } finally {
      setIsSuggestionLoading(false);
    }
  };
  
  // Close suggestion modal
  const closeSuggestionModal = () => {
    // Stop any playing audio when closing modal
    stopAudio();
    setSelectedSuggestion(null);
    setSuggestionResponse(null);
  };
  
  // Play AI recommendation as audio using Web Speech API
  const playAudio = () => {
    if (!suggestionResponse || isAudioPlaying) return;
    
    // Check if speech synthesis is supported
    if (!('speechSynthesis' in window)) {
      toast.error('Text-to-speech is not supported in your browser');
      return;
    }
    
    // Cancel any ongoing speech and wait a moment
    window.speechSynthesis.cancel();
    
    // Function to start speaking after voices are ready
    const startSpeaking = () => {
      // Create new utterance
      const utterance = new SpeechSynthesisUtterance(suggestionResponse);
      utterance.rate = 1.0; // Normal speed
      utterance.pitch = 1.0; // Normal pitch
      utterance.volume = 1.0; // Full volume
      
      // Try to use a natural-sounding voice
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Google') || 
        voice.name.includes('Samantha') || 
        voice.name.includes('Natural')
      ) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      // Event handlers
      utterance.onstart = () => {
        setIsAudioPlaying(true);
      };
      
      utterance.onend = () => {
        setIsAudioPlaying(false);
        speechSynthRef.current = null;
      };
      
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setIsAudioPlaying(false);
        speechSynthRef.current = null;
        // Only show error for actual failures, not interruptions or cancellations
        if (event.error !== 'canceled' && event.error !== 'interrupted') {
          toast.error('Failed to play audio');
        }
      };
      
      // Store reference and start speaking
      speechSynthRef.current = utterance;
      
      // Small delay to ensure stability after cancel
      setTimeout(() => {
        window.speechSynthesis.speak(utterance);
      }, 100);
    };
    
    // Check if voices are already loaded
    const voices = window.speechSynthesis.getVoices();
    if (voices.length > 0) {
      startSpeaking();
    } else {
      // Wait for voices to load (Chrome loads them asynchronously)
      window.speechSynthesis.onvoiceschanged = () => {
        startSpeaking();
      };
      // Fallback: try anyway after a short delay
      setTimeout(() => {
        if (!isAudioPlaying) {
          startSpeaking();
        }
      }, 500);
    }
  };
  
  // Stop audio playback
  const stopAudio = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsAudioPlaying(false);
    speechSynthRef.current = null;
  };

  const fetchFhir = useCallback(async (url, init = {}) => {
    const baseHeaders = {
      'Content-Type': 'application/fhir+json',
      ...(init.headers || {}),
    };

    let response = await fetch(url, {
      ...init,
      headers: await buildFhirHeaders(baseHeaders, false),
    });

    if (response.status === 401) {
      response = await fetch(url, {
        ...init,
        headers: await buildFhirHeaders(baseHeaders, true),
      });
    }

    return response;
  }, []);
  
  // Fetch observations and generate suggestions
  const fetchObservationsForSuggestions = useCallback(async () => {
    if (!authUser) return;
    
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const query = `${fhirEndpoint}/Observation?identifier=${encodeURIComponent(`${fhirSystemBase}/fhir/ids|${authUser.uid}`)}&date=ge${thirtyDaysAgo.toISOString()}&_sort=-date&_count=500`;
      
      const response = await fetchFhir(query);
      
      if (response.ok) {
        const bundle = await response.json();
        setRawObservations(bundle);
        const suggestions = generateAISuggestions(bundle);
        setAiSuggestions(suggestions);
      }
    } catch (error) {
      console.error('Error fetching observations for suggestions:', error);
    }
  }, [authUser, fetchFhir, fhirEndpoint, generateAISuggestions]);

  // ============= CIRCADIAN AI ASSISTANT FUNCTIONS =============
  
  // Helper: Show in-app notification (fallback when browser notifications fail)
  const showInAppNotification = (title, message, taskId = null) => {
    const notification = {
      id: Date.now(),
      title,
      message,
      taskId,
      timestamp: new Date()
    };
    
    setInAppNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 30 seconds (longer than browser notifications)
    setTimeout(() => {
      setInAppNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 30000);
    
    // Also show as toast for immediate visibility
    toast.info(`${title}: ${message}`, {
      autoClose: 10000,
      closeButton: true,
      draggable: true
    });
    
    console.log('📱 In-app notification shown:', title);
  };
  
  // Helper: Remove in-app notification
  const dismissInAppNotification = (notificationId) => {
    setInAppNotifications(prev => prev.filter(n => n.id !== notificationId));
  };
  
  // 1. Web Push Notification Setup
  const setupPushNotifications = async () => {
    try {
      console.log('🔔 Setting up notifications...')
      
      // Check if notifications are supported
      if (!('Notification' in window)) {
        toast.error('Notifications are not supported in your browser')
        return false
      }

      // Request permission
      const permission = await Notification.requestPermission()
      console.log('📋 Permission status:', permission)
      
      if (permission !== 'granted') {
        toast.warning('Notification permission denied')
        return false
      }

      // Enable notifications (local/browser-based)
      setNotificationsEnabled(true)
      localStorage.setItem('notificationsEnabled', 'true')
      toast.success('Notifications enabled!')
      console.log('✅ Notifications enabled successfully')
      
      // Send a test notification
      try {
        new Notification('Circadian AI Assistant Activated! 🎉', {
          body: 'You\'ll receive reminders for upcoming tasks. The assistant checks every minute.',
          icon: '/favicon.ico'
        })
      } catch (notifError) {
        console.warn('Test notification failed:', notifError)
      }

      // Try to register service worker for enhanced notifications
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          console.log('✅ Service Worker ready:', registration);
          
          // Subscribe to push notifications if supported
          if ('PushManager' in window) {
            try {
              const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: import.meta.env.VITE_VAPID_PUBLIC_KEY
              });
              setPushSubscription(subscription);
              console.log('✅ Push subscription created:', subscription);
            } catch (pushError) {
              console.warn('⚠️ Push subscription failed (optional):', pushError.message);
            }
          }
        } catch (swError) {
          console.warn('⚠️ Service Worker not available:', swError.message);
        }
      }
      
      return true
    } catch (error) {
      console.error('❌ Error setting up notifications:', error)
      toast.error('Failed to enable notifications')
      return false
    }
  }

  // 1b. Toggle Notifications On/Off
  const toggleNotifications = async () => {
    if (notificationsEnabled) {
      // Disable notifications
      setNotificationsEnabled(false);
      localStorage.setItem('notificationsEnabled', 'false');
      toast.info('Notifications disabled - enable them again anytime');
      console.log('🔕 Notifications disabled by user');
    } else {
      // Re-enable notifications - check browser permission first
      if (!('Notification' in window)) {
        toast.error('Notifications are not supported in your browser');
        return;
      }
      
      console.log('🔔 Re-enabling notifications...');
      console.log('Current permission:', Notification.permission);
      
      if (Notification.permission === 'granted') {
        // Permission already granted, just enable
        setNotificationsEnabled(true);
        localStorage.setItem('notificationsEnabled', 'true');
        toast.success('Notifications re-enabled!');
        console.log('✅ Notifications re-enabled (permission already granted)');
        
        // Send confirmation notification
        try {
          new Notification('🔔 Notifications Re-enabled', {
            body: 'Task reminders are now active again!',
            icon: '/favicon.ico'
          });
        } catch (error) {
          console.warn('Confirmation notification failed:', error);
        }
      } else if (Notification.permission === 'denied') {
        // Permission denied - user needs to manually enable in browser settings
        toast.error('Notification permission is blocked. Please enable in browser settings.');
        console.error('❌ Notification permission denied - cannot enable');
      } else {
        // Permission not yet requested, ask for it
        await setupPushNotifications();
      }
    }
  };

  // 2. Create FHIR Task from AI-extracted metadata
  const createFHIRTask = async (noteId, metadata) => {
    try {
      if (!authUser || !metadata.dueTime) return null

      // First, ensure Patient resource exists
      console.log('🔍 Checking if Patient resource exists...');
      const patientCheckResponse = await fetchFhir(
        `${fhirEndpoint}/Patient?identifier=${encodeURIComponent(`${fhirSystemBase}/fhir/ids|${authUser.uid}`)}`
      );

      let patientId = null;
      if (patientCheckResponse.ok) {
        const bundle = await patientCheckResponse.json();
        if (bundle.entry && bundle.entry.length > 0) {
          patientId = bundle.entry[0].resource.id;
          console.log('✅ Patient resource exists:', patientId);
        }
      }

      // If Patient doesn't exist, create it
      if (!patientId) {
        console.log('📝 Creating Patient resource...');
        const patientResource = {
          resourceType: 'Patient',
          identifier: [{
            system: `${fhirSystemBase}/fhir/ids`,
            value: authUser.uid
          }],
          name: [{
            text: authUser.displayName || authUser.email || 'User',
            given: [authUser.displayName?.split(' ')[0] || authUser.email?.split('@')[0] || 'User'],
            family: authUser.displayName?.split(' ').slice(1).join(' ') || 'User'
          }],
          active: true
        };

        const createPatientResponse = await fetchFhir(`${fhirEndpoint}/Patient`, {
          method: 'POST',
          body: JSON.stringify(patientResource)
        });

        if (!createPatientResponse.ok) {
          const errorText = await createPatientResponse.text();
          console.error('Failed to create Patient:', errorText);
          throw new Error(`Failed to create Patient resource: ${createPatientResponse.status}`);
        }

        const createdPatient = await createPatientResponse.json();
        patientId = createdPatient.id;
        console.log('✅ Created Patient resource:', patientId);
      }

      // Now create the Task with correct Patient reference
      const taskResource = ensureFhirUserIdentifier({
        resourceType: 'Task',
        status: 'requested',
        intent: 'order',
        priority: metadata.isUrgent ? 'urgent' : 'routine',
        description: metadata.task,
        for: {
          reference: `Patient/${patientId}`
        },
        subject: {
          reference: `Patient/${patientId}`
        },
        authoredOn: new Date().toISOString(),
        executionPeriod: {
          end: metadata.dueTime
        },
        identifier: [{
          system: `${fhirSystemBase}/fhir/notes`,
          value: noteId
        }],
        code: {
          coding: [{
            system: 'http://hl7.org/fhir/CodeSystem/task-code',
            code: metadata.category || 'fulfill',
            display: metadata.category || 'Fulfill'
          }]
        },
        input: metadata.relatedMetrics?.map(metric => ({
          type: {
            text: 'relatedMetric'
          },
          valueString: metric
        })) || []
      }, authUser.uid)

      console.log('📤 Creating FHIR Task:', taskResource);

      const response = await fetchFhir(`${fhirEndpoint}/Task`, {
        method: 'POST',
        body: JSON.stringify(taskResource)
      })

      if (!response.ok) {
        const errorText = await response.text();
        console.error('FHIR Task creation failed:', response.status, errorText);
        throw new Error(`Failed to create FHIR Task: ${response.status} - ${errorText}`)
      }

      const result = await response.json()
      console.log('✅ Created FHIR Task:', result)
      
      // Immediately verify the task exists by querying for it
      console.log('🔍 Verifying task creation - querying for Task ID:', result.id);
      const verifyResponse = await fetchFhir(`${fhirEndpoint}/Task/${result.id}`);
      
      if (verifyResponse.ok) {
        const verifiedTask = await verifyResponse.json();
        console.log('✅ Task verified - FULL TASK:', verifiedTask);
        console.log('🔍 Task reference fields:', {
          id: verifiedTask.id,
          status: verifiedTask.status,
          subject: verifiedTask.subject,
          for: verifiedTask.for,
          focus: verifiedTask.focus,
          patient: verifiedTask.patient,
          owner: verifiedTask.owner,
          executionPeriod: verifiedTask.executionPeriod
        });
      } else {
        console.warn('⚠️ Task verification failed - might not be immediately available');
      }
      
      return result
    } catch (error) {
      console.error('❌ Error creating FHIR Task:', error)
      toast.error('Failed to create task reminder in FHIR');
      return null
    }
  }

  // 3. Query FHIR for pending tasks
  const fetchPendingTasks = async () => {
    try {
      if (!authUser) return []

      // First, get the Patient FHIR ID
      console.log('🔍 Looking up Patient FHIR ID...');
      const patientCheckResponse = await fetchFhir(
        `${fhirEndpoint}/Patient?identifier=${encodeURIComponent(`${fhirSystemBase}/fhir/ids|${authUser.uid}`)}`
      );

      let patientId = null;
      if (patientCheckResponse.ok) {
        const bundle = await patientCheckResponse.json();
        if (bundle.entry && bundle.entry.length > 0) {
          patientId = bundle.entry[0].resource.id;
          console.log('✅ Patient FHIR ID:', patientId);
        } else {
          console.warn('⚠️ No Patient resource found - tasks cannot be retrieved until Patient exists');
          return [];
        }
      } else {
        console.warn('⚠️ Failed to fetch Patient resource');
        return [];
      }

      // Query for active tasks using the Patient's FHIR ID
      const queries = [
        // Try searching by identifier - this should work regardless of reference field
        `${fhirEndpoint}/Task?identifier=${encodeURIComponent(`${fhirSystemBase}/fhir/notes|`)}&status=requested,in-progress&_sort=-_lastUpdated`,
        // Try identifier without status filter
        `${fhirEndpoint}/Task?identifier=${encodeURIComponent(`${fhirSystemBase}/fhir/notes|`)}&_sort=-_lastUpdated&_count=100`,
        // Try subject parameter with Patient ID
        `${fhirEndpoint}/Task?subject=Patient/${patientId}&status=requested,in-progress&_sort=-_lastUpdated`,
        // Try subject parameter with Patient ID - NO STATUS FILTER (diagnostic)
        `${fhirEndpoint}/Task?subject=Patient/${patientId}&_sort=-_lastUpdated&_count=20`,
        // Try focus parameter (some servers use this for the primary reference)
        `${fhirEndpoint}/Task?focus=Patient/${patientId}&status=requested,in-progress&_sort=-_lastUpdated`,
        // Try patient parameter with Patient ID
        `${fhirEndpoint}/Task?patient=Patient/${patientId}&status=requested,in-progress&_sort=-_lastUpdated`,
        // Try owner parameter (in case tasks are assigned to patient as owner)
        `${fhirEndpoint}/Task?owner=Patient/${patientId}&status=requested,in-progress&_sort=-_lastUpdated`,
        // Fallback: just get all tasks for this user (server should filter by auth)
        `${fhirEndpoint}/Task?status=requested,in-progress&_sort=-_lastUpdated&_count=100`,
        // Last resort: get ALL tasks
        `${fhirEndpoint}/Task?_sort=-_lastUpdated&_count=100`
      ]

      let bundle = null
      let lastError = null

      // Try different query parameters
      for (const queryUrl of queries) {
        try {
          const response = await fetchFhir(queryUrl)

          if (response.ok) {
            bundle = await response.json()
            console.log('✅ Successfully fetched tasks with query:', queryUrl)
            break
          } else {
            const errorText = await response.text()
            console.warn(`⚠️ Query failed (${response.status}): ${queryUrl}`, errorText)
            lastError = new Error(`Status ${response.status}: ${errorText}`)
          }
        } catch (fetchError) {
          console.warn('⚠️ Fetch error for query:', queryUrl, fetchError.message)
          lastError = fetchError
        }
      }

      // If all queries failed, return empty array (no tasks yet)
      if (!bundle) {
        console.warn('ℹ️ No tasks found with any query - this is normal if you haven\'t created any tasks yet');
        console.warn('Last error:', lastError?.message);
        return []
      }
      
      if (bundle.entry && bundle.entry.length > 0) {
        console.log(`📦 Raw bundle contains ${bundle.entry.length} task(s)`);
        
        const taskList = bundle.entry.map(entry => {
          const task = entry.resource;
          console.log('📋 Task in bundle:', {
            id: task.id,
            status: task.status,
            description: task.description,
            subject: task.subject,
            for: task.for,
            dueTime: task.executionPeriod?.end
          });
          
          const parsedTask = {
            id: task.id,
            description: task.description,
            dueTime: task.executionPeriod?.end,
            isUrgent: task.priority === 'urgent',
            status: task.status,
            category: task.code?.coding?.[0]?.code,
            noteId: task.identifier?.find(i => i.system === `${fhirSystemBase}/fhir/notes`)?.value
          }
          return parsedTask;
        })
        
        // Filter only requested or in-progress tasks
        const activeTasks = taskList.filter(t => 
          t.status === 'requested' || t.status === 'in-progress'
        );
        console.log(`✅ Found ${activeTasks.length} pending task(s) (out of ${taskList.length} total):`, activeTasks);
        return activeTasks;
      }
      
      console.log('ℹ️ Bundle returned but no entries (total:', bundle.total, ')');
      return []
    } catch (error) {
      console.error('❌ Error fetching pending tasks:', error)
      return []
    }
  }

  // 4. Enhanced Health Metrics Analysis with Comprehensive Monitoring
  // Uses a SINGLE bundle request to fetch all observations, then processes locally
  const analyzeHealthMetrics = async () => {
    try {
      if (!authUser) return []

      const insights = []
      const now = new Date()
      
      // Get date range for last 30 days
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      // LOINC codes for health metrics (must match what's stored in FHIR)
      const METRIC_CONFIG = {
        'Heart Rate': { loinc: '8867-4', category: 'physical', type: 'heartRate' },
        'Body Temperature': { loinc: '8310-5', category: 'physical', type: 'temperature' },
        'Oxygen Saturation': { loinc: '59408-5', category: 'physical', type: 'oxygenSat' },
        'Blood Glucose': { loinc: '2339-0', category: 'physical', type: 'bloodGlucose' },
        'Steps Taken': { loinc: '55423-8', category: 'activity', type: 'steps' },
        'Sleep Hours': { loinc: '93832-4', category: 'wellness', type: 'sleep' },
        'Weight': { loinc: '29463-7', category: 'physical', type: 'weight' },
        'Calories': { loinc: '41981-2', category: 'nutrition', type: 'calories' },
        'Water Intake': { loinc: '77255-5', category: 'wellness', type: 'hydration' },
        'Blood Pressure Systolic': { loinc: '8480-6', category: 'physical', type: 'bloodPressure' },
        'Blood Pressure Diastolic': { loinc: '8462-4', category: 'physical', type: 'bloodPressure' },
      };
      
      // Helper function to calculate days since last observation
      const getDaysSince = (dateString) => {
        if (!dateString) return Infinity;
        const date = new Date(dateString);
        return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
      };

      // Helper function to get suggested actions
      const getSuggestedAction = (type, daysSince) => {
        const suggestions = {
          weight: daysSince > 14 ? 'Consider scheduling a health checkup' : 'Try weighing yourself daily for better tracking',
          steps: daysSince > 7 ? 'Start with short walks and gradually increase' : 'Aim for 10,000 steps daily',
          calories: daysSince > 7 ? 'Use a food tracking app to monitor intake' : 'Track meals to understand eating patterns',
          sleep: daysSince > 7 ? 'Establish a consistent bedtime routine' : 'Aim for 7-9 hours of quality sleep',
          hydration: daysSince > 3 ? 'Set hourly water reminders' : 'Aim for 8 glasses of water daily',
          heartRate: 'Monitor your resting heart rate for cardiovascular health',
          bloodGlucose: 'Track blood glucose regularly if you have diabetes risk factors',
          temperature: 'Track temperature if feeling unwell',
          oxygenSat: 'Monitor oxygen levels during exercise or illness',
          bloodPressure: 'Regular blood pressure monitoring helps detect hypertension early',
        };
        return suggestions[type] || 'Consider tracking this metric regularly';
      };

      // SINGLE BUNDLE REQUEST: Fetch all observations for the user in one call
      // This is much more efficient than multiple individual requests
      const bundleQuery = `${fhirEndpoint}/Observation?identifier=${encodeURIComponent(`${fhirSystemBase}/fhir/ids|${authUser.uid}`)}&date=ge${thirtyDaysAgo.toISOString()}&_sort=-date&_count=500`;
      
      console.log('🔍 Fetching all observations in single bundle request...');
      
      const response = await fetchFhir(bundleQuery);

      if (!response.ok) {
        console.error('Failed to fetch observations bundle:', response.status);
        return [];
      }

      const bundle = await response.json();
      console.log(`📦 Received bundle with ${bundle.entry?.length || 0} observations`);

      // Process the bundle locally - group by observation type
      const observationsByType = {};
      
      if (bundle.entry && bundle.entry.length > 0) {
        bundle.entry.forEach(entry => {
          const obs = entry.resource;
          // Get the display name from the observation code
          const displayName = obs.code?.coding?.[0]?.display || obs.code?.text || 'Unknown';
          const loincCode = obs.code?.coding?.[0]?.code;
          
          // Initialize array for this type if not exists
          if (!observationsByType[displayName]) {
            observationsByType[displayName] = [];
          }
          
          observationsByType[displayName].push({
            date: obs.effectiveDateTime,
            value: obs.valueQuantity?.value,
            unit: obs.valueQuantity?.unit || '',
            loinc: loincCode,
            display: displayName
          });
        });
        
        // Sort each type by date (newest first)
        Object.keys(observationsByType).forEach(type => {
          observationsByType[type].sort((a, b) => new Date(b.date) - new Date(a.date));
        });
      }
      
      console.log('📊 Observations grouped by type:', Object.keys(observationsByType));

      // Generate insights based on available data
      const processedTypes = new Set();
      
      // Check each metric type and generate appropriate insights
      Object.entries(METRIC_CONFIG).forEach(([displayName, config]) => {
        const observations = observationsByType[displayName];
        
        if (observations && observations.length > 0) {
          // Has data - check if it's stale
          const latest = observations[0];
          const daysSince = getDaysSince(latest.date);
          
          // Only add insight if data is stale (more than threshold days old)
          const staleThresholds = {
            steps: 1, calories: 1, sleep: 1, hydration: 1,
            heartRate: 3, weight: 7, bloodGlucose: 3, temperature: 7,
            oxygenSat: 7, bloodPressure: 7
          };
          
          const threshold = staleThresholds[config.type] || 3;
          
          if (daysSince > threshold) {
            let priority = 'low';
            if (daysSince > 14) priority = 'high';
            else if (daysSince > 7) priority = 'medium';
            
            insights.push({
              type: config.type,
              message: `Last ${displayName}: ${latest.value} ${latest.unit} (${daysSince} days ago). Consider updating your tracking.`,
              priority,
              daysSince,
              category: config.category,
              hasData: true,
              latestValue: latest.value,
              suggestedAction: getSuggestedAction(config.type, daysSince)
            });
          }
          
          processedTypes.add(config.type);
        }
      });
      
      // Add insights for completely missing metrics (only key ones)
      const criticalMissingMetrics = [
        { type: 'weight', display: 'Weight', category: 'physical' },
        { type: 'steps', display: 'Steps', category: 'activity' },
        { type: 'sleep', display: 'Sleep', category: 'wellness' },
      ];
      
      criticalMissingMetrics.forEach(metric => {
        // Check if we have ANY observation matching this type
        const hasData = Object.keys(observationsByType).some(key => 
          key.toLowerCase().includes(metric.type.toLowerCase()) ||
          METRIC_CONFIG[key]?.type === metric.type
        );
        
        if (!hasData && !processedTypes.has(metric.type)) {
          insights.push({
            type: metric.type,
            message: `No ${metric.display.toLowerCase()} data found. Consider tracking for better health awareness.`,
            priority: 'medium',
            daysSince: Infinity,
            category: metric.category,
            hasData: false,
            latestValue: null,
            suggestedAction: getSuggestedAction(metric.type, Infinity)
          });
        }
      });

      // If we have lots of recent data, add a positive insight
      if (bundle.entry && bundle.entry.length > 10) {
        const recentCount = bundle.entry.filter(e => {
          const date = new Date(e.resource.effectiveDateTime);
          return getDaysSince(date) <= 7;
        }).length;
        
        if (recentCount > 5) {
          insights.unshift({
            type: 'summary',
            message: `Great job! You've logged ${recentCount} health observations in the past week. Keep up the good tracking!`,
            priority: 'low',
            daysSince: 0,
            category: 'general',
            hasData: true,
            latestValue: recentCount,
            suggestedAction: 'Continue your consistent tracking habits'
          });
        }
      }

      // Sort insights by priority (high first) and days since
      insights.sort((a, b) => {
        const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return b.daysSince - a.daysSince;
      });

      console.log(`🔍 Health analysis complete: ${insights.length} insights generated from ${bundle.entry?.length || 0} observations`);
      return insights;
    } catch (error) {
      console.error('Error analyzing health metrics:', error);
      return [];
    }
  }

  // 5. Generate helpful topic suggestions based on health patterns
  const generateTopicSuggestions = (insights) => {
    const suggestions = [];
    const now = new Date();
    const hour = now.getHours();
    
    // Time-based suggestions
    const timeBasedSuggestions = {
      morning: [
        { message: "Good morning! Consider tracking your weight and setting daily goals.", category: "wellness" },
        { message: "Start your day with a healthy breakfast and hydration check.", category: "nutrition" },
        { message: "Morning is perfect for planning your day and setting priorities.", category: "productivity" }
      ],
      afternoon: [
        { message: "Midday check: How are you feeling? Consider logging your mood.", category: "mental-health" },
        { message: "Afternoon energy dip? Track your activity levels and consider a walk.", category: "activity" },
        { message: "Lunch break is a great time to log your meals and calories.", category: "nutrition" }
      ],
      evening: [
        { message: "Evening wind-down: Consider tracking your sleep preparation routine.", category: "wellness" },
        { message: "Reflect on your day - how did your tasks and activities go?", category: "productivity" },
        { message: "Prepare for better sleep by tracking your evening routine.", category: "wellness" }
      ]
    };

    // Determine time of day
    let timeOfDay = 'morning';
    if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
    else if (hour >= 17) timeOfDay = 'evening';

    // Add time-based suggestions
    const timeSuggestions = timeBasedSuggestions[timeOfDay];
    if (timeSuggestions) {
      suggestions.push(...timeSuggestions);
    }

    // Insight-based suggestions
    insights.forEach(insight => {
      switch (insight.type) {
        case 'weight':
          suggestions.push({
            message: "Weight tracking tip: Weigh yourself at the same time daily for consistent measurements.",
            category: "physical"
          });
          break;
        case 'steps':
          suggestions.push({
            message: "Step goal ideas: Take stairs instead of elevators, park farther away, or take walking meetings.",
            category: "activity"
          });
          break;
        case 'calories':
          suggestions.push({
            message: "Nutrition tip: Focus on whole foods and portion control for better calorie management.",
            category: "nutrition"
          });
          break;
        case 'sleep':
          suggestions.push({
            message: "Sleep hygiene: Keep your bedroom cool, dark, and quiet for better rest.",
            category: "wellness"
          });
          break;
        case 'activity':
          suggestions.push({
            message: "Activity variety: Try different exercises to keep your routine interesting and effective.",
            category: "activity"
          });
          break;
        case 'tasks':
          suggestions.push({
            message: "Productivity hack: Use the Pomodoro Technique - work for 25 minutes, then take a 5-minute break.",
            category: "productivity"
          });
          break;
        case 'mood':
          suggestions.push({
            message: "Mood tracking: Note what activities or situations affect your mood positively or negatively.",
            category: "mental-health"
          });
          break;
        case 'hydration':
          suggestions.push({
            message: "Hydration reminder: Start your day with a glass of water and keep a water bottle nearby.",
            category: "wellness"
          });
          break;
      }
    });

    // General wellness suggestions
    const generalSuggestions = [
      { message: "Consider tracking your stress levels and what triggers them.", category: "mental-health" },
      { message: "Meditation and mindfulness can improve both mental and physical health.", category: "wellness" },
      { message: "Regular health checkups are important - schedule your next appointment.", category: "physical" },
      { message: "Social connections are vital for mental health - reach out to friends or family.", category: "mental-health" },
      { message: "Learning new skills can boost your mood and cognitive health.", category: "mental-health" },
      { message: "Outdoor time and natural light exposure can improve sleep and mood.", category: "wellness" }
    ];

    suggestions.push(...generalSuggestions);

    // Remove duplicates and return random selection
    const uniqueSuggestions = suggestions.filter((suggestion, index, self) => 
      index === self.findIndex(s => s.message === suggestion.message)
    );

    // Return 1-3 random suggestions
    const numSuggestions = Math.min(3, uniqueSuggestions.length);
    const shuffled = uniqueSuggestions.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, numSuggestions);
  };

  // 6. Circadian Check - runs every minute
  const performCircadianCheck = useCallback(async () => {
    try {
      if (!authUser || !notificationsEnabled) return

      const now = new Date()
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60000)
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60000)

      console.log('⏰ Circadian check at:', now.toLocaleTimeString());

      // Fetch pending tasks
      const pendingTasks = await fetchPendingTasks()
      console.log(`📋 Checking ${pendingTasks.length} pending tasks for notifications`);
      setTasks(pendingTasks)

      // Check for imminent tasks (due within 5 minutes) OR recently overdue (up to 5 min ago)
      const imminentTasks = pendingTasks.filter(task => {
        if (!task.dueTime) return false
        const dueDate = new Date(task.dueTime)
        
        // Notify if: due in next 5 min OR was due in last 5 min (catch missed notifications)
        const isImminent = dueDate >= now && dueDate <= fiveMinutesFromNow;
        const isRecentlyOverdue = dueDate >= fiveMinutesAgo && dueDate < now;
        
        if (isImminent || isRecentlyOverdue) {
          console.log(`🔔 Task ${task.id} ready for notification:`, {
            description: task.description,
            dueTime: task.dueTime,
            now: now.toISOString(),
            isImminent,
            isRecentlyOverdue
          });
        }
        
        return isImminent || isRecentlyOverdue;
      })

      console.log(`🔔 Found ${imminentTasks.length} task(s) ready for notification`);

      // Send notifications for imminent tasks
      imminentTasks.forEach(task => {
        // Check if we already notified for this task (using localStorage to track)
        const notifiedKey = `task_notified_${task.id}`;
        const alreadyNotified = localStorage.getItem(notifiedKey);
        
        if (alreadyNotified) {
          console.log(`⏭️ Already notified for task ${task.id}, skipping`);
          return;
        }
        
        // Format due time for display
        const dueTime = new Date(task.dueTime);
        const timeStr = dueTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
        const messageBody = `${task.description}\n\nDue: ${timeStr}`;
        
        let notificationShown = false;
        
        // Try browser notification first (if permission granted)
        if (Notification.permission === 'granted') {
          console.log(`📢 Attempting browser notification for task ${task.id}:`, task.description);
          
          try {
            const notification = new Notification('⏰ Task Reminder', {
              body: messageBody,
              icon: '/favicon.ico',
              badge: '/favicon.ico',
              tag: `task-${task.id}`,
              requireInteraction: true,
              silent: false
            });
            
            console.log('✅ Browser notification object created:', notification);
            
            notification.onshow = () => {
              console.log('✅ Browser notification displayed for task', task.id);
            };
            
            notification.onerror = (error) => {
              console.error('❌ Browser notification error for task', task.id, error);
              // Fallback to in-app if browser notification fails
              showInAppNotification('⏰ Task Reminder', messageBody, task.id);
            };
            
            notification.onclick = () => {
              console.log('📌 Notification clicked - focusing window');
              window.focus();
              notification.close();
            };
            
            notification.onclose = () => {
              console.log('🔕 Notification closed for task', task.id);
            };
            
            notificationShown = true;
          } catch (error) {
            console.error('❌ Failed to create browser notification:', error);
            notificationShown = false;
          }
        }
        
        // Fallback to in-app notification if browser notification not shown
        if (!notificationShown) {
          console.log('📱 Showing in-app notification (browser notification unavailable)');
          showInAppNotification('⏰ Task Reminder', messageBody, task.id);
        }
        
        // Mark as notified
        localStorage.setItem(notifiedKey, new Date().toISOString());
      })

      // Check health metrics every 5 minutes (on minute % 5 === 0)
      if (now.getMinutes() % 5 === 0) {
        const insights = await analyzeHealthMetrics()
        setHealthInsights(insights)
        
        // Notify for high priority health insights
        insights.filter(i => i.priority === 'high').forEach(insight => {
          if (Notification.permission === 'granted') {
            new Notification('🏥 Health Reminder', {
              body: `${insight.message}\n\n💡 Suggestion: ${insight.suggestedAction}`,
              icon: '/favicon.ico',
              tag: `health-${insight.type}`,
              requireInteraction: true
            })
          } else {
            // Fallback to in-app notification
            showInAppNotification('🏥 Health Reminder', 
              `${insight.message}\n\n💡 Suggestion: ${insight.suggestedAction}`, 
              `health-${insight.type}`);
          }
        })

        // Send medium priority insights as less intrusive notifications
        insights.filter(i => i.priority === 'medium').forEach(insight => {
          if (Notification.permission === 'granted') {
            new Notification('📊 Health Check', {
              body: `${insight.message}\n\n💡 Tip: ${insight.suggestedAction}`,
              icon: '/favicon.ico',
              tag: `health-${insight.type}`,
              silent: true
            })
          }
        })
      }

      // Check for helpful topic suggestions every 15 minutes (on minute % 15 === 0)
      if (now.getMinutes() % 15 === 0) {
        const topicSuggestions = generateTopicSuggestions(await analyzeHealthMetrics());
        if (topicSuggestions.length > 0) {
          const suggestion = topicSuggestions[0]; // Show the most relevant suggestion
          if (Notification.permission === 'granted') {
            new Notification('💡 Health Tip', {
              body: suggestion.message,
              icon: '/favicon.ico',
              tag: `tip-${suggestion.category}`,
              silent: true
            })
          } else {
            showInAppNotification('💡 Health Tip', suggestion.message, `tip-${suggestion.category}`);
          }
        }
      }
    } catch (error) {
      console.error('Error in circadian check:', error)
    }
  }, [authUser, notificationsEnabled, fhirEndpoint])

  // Initialize PWA features
  useEffect(() => {
    checkPwaStatus();
    handlePwaInstallPrompt();
  }, []);
  
  // Fetch observations for AI suggestions on mount
  useEffect(() => {
    if (authUser) {
      fetchObservationsForSuggestions();
    }
  }, [authUser, fetchObservationsForSuggestions]);

  // Set up circadian check interval
  useEffect(() => {
    if (notificationsEnabled && authUser) {
      console.log('Starting Circadian AI Assistant...');
      
      // Run immediately
      performCircadianCheck();
      
      // Then run every minute
      circadianCheckInterval.current = setInterval(performCircadianCheck, 60000);

      // Cleanup on unmount or when notifications disabled
      return () => {
        if (circadianCheckInterval.current) {
          console.log('Stopping Circadian AI Assistant...');
          clearInterval(circadianCheckInterval.current);
        }
      };
    }
  }, [notificationsEnabled, authUser, performCircadianCheck]);

  // Manual refresh function
  const handleManualRefresh = async () => {
    console.log('🔄 Manually refreshing tasks...');
    const updatedTasks = await fetchPendingTasks();
    setTasks(updatedTasks);
    toast.info(`Found ${updatedTasks.length} task(s)`);
    
    // Also trigger parent refresh if callback provided
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <>
      {/* Inline CSS for notification animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @media (max-width: 768px) {
          .in-app-notification-container {
            left: 10px !important;
            right: 10px !important;
            max-width: calc(100% - 20px) !important;
          }
        }
      `}</style>
      
      {/* In-App Notification Overlay (Top-Right Corner) */}
      {inAppNotifications.length > 0 && (
        <div 
          className="in-app-notification-container"
          style={{
            position: 'fixed',
            top: '80px',
            right: '20px',
            zIndex: 9999,
            maxWidth: '400px',
            width: '100%'
          }}
        >
          {inAppNotifications.map(notif => (
            <CCard 
              key={notif.id}
              className="mb-2"
              style={{
                backgroundColor: '#fff',
                border: '2px solid #4CAF50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                animation: 'slideInRight 0.3s ease-out'
              }}
            >
              <CCardHeader className="d-flex justify-content-between align-items-center bg-success text-white">
                <strong>⏰ {notif.title}</strong>
                <CButton
                  size="sm"
                  color="light"
                  onClick={() => dismissInAppNotification(notif.id)}
                  style={{ padding: '2px 8px' }}
                >
                  ✕
                </CButton>
              </CCardHeader>
              <CCardBody>
                <div style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
                  {notif.message}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                  {notif.timestamp.toLocaleTimeString()}
                </div>
              </CCardBody>
            </CCard>
          ))}
        </div>
      )}

      {/* Circadian AI Assistant Card */}
      <CCard className="mb-3">
        <CCardBody>
          <h5 className="mb-3">
            <CIcon icon={cilBell} className="me-2" />
            Circadian AI Assistant
          </h5>
          <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
            <CButton
              color={notificationsEnabled ? 'success' : 'primary'}
              onClick={toggleNotifications}
            >
              <CIcon icon={cilBell} className="me-2" />
              {notificationsEnabled ? '🔔 Notifications ON' : '🔕 Enable Notifications'}
            </CButton>
            {notificationsEnabled && (
              <CBadge color="success">
                Checking every minute
              </CBadge>
            )}
            {!isPwaInstalled && pwaInstallPrompt && (
              <CButton
                color="info"
                size="sm"
                onClick={installPwa}
              >
                📱 Install App
              </CButton>
            )}
            {isPwaInstalled && (
              <CBadge color="info">
                📱 PWA Installed
              </CBadge>
            )}
            <CButton
              color="info"
              size="sm"
              onClick={handleManualRefresh}
            >
              Refresh Tasks
            </CButton>
            <CButton
              color="success"
              size="sm"
              onClick={async () => {
                console.log('🔍 Running health analysis...');
                const insights = await analyzeHealthMetrics();
                setHealthInsights(insights);
                toast.success(`Found ${insights.length} health insights`);
                
                // Show topic suggestions
                const suggestions = generateTopicSuggestions(insights);
                if (suggestions.length > 0) {
                  const suggestion = suggestions[0];
                  toast.info(`💡 ${suggestion.message}`, {
                    autoClose: 8000,
                    closeButton: true
                  });
                }
              }}
            >
              🔍 Analyze Health
            </CButton>
            <CButton
              color="warning"
              size="sm"
              onClick={() => {
                console.log('🧪 Testing notification system...');
                console.log('Notification permission:', Notification.permission);
                console.log('Notifications enabled:', notificationsEnabled);
                console.log('Current tasks:', tasks);
                
                if (Notification.permission === 'granted') {
                  try {
                    const testNotif = new Notification('🧪 Test Notification', {
                      body: 'If you see this, notifications are working!\n\nClick to close.',
                      icon: '/favicon.ico',
                      tag: 'test-notification',
                      requireInteraction: true
                    });
                    
                    testNotif.onshow = () => {
                      console.log('✅ TEST notification displayed!');
                      toast.success('Test notification displayed!');
                    };
                    
                    testNotif.onerror = (error) => {
                      console.error('❌ TEST notification error:', error);
                      toast.error('Test notification failed');
                    };
                    
                    testNotif.onclick = () => {
                      console.log('📌 Test notification clicked');
                      window.focus();
                      testNotif.close();
                    };
                    
                    console.log('✅ Test notification created:', testNotif);
                  } catch (error) {
                    console.error('❌ Failed to create test notification:', error);
                    toast.error('Failed to create notification');
                  }
                } else {
                  toast.error('Notification permission not granted');
                  console.error('Notification permission:', Notification.permission);
                }
              }}
            >
              🧪 Test Notification
            </CButton>
            <CButton
              color="danger"
              size="sm"
              onClick={() => {
                // Clear all notification tracking from localStorage
                const keys = Object.keys(localStorage);
                const notificationKeys = keys.filter(k => k.startsWith('task_notified_'));
                notificationKeys.forEach(key => localStorage.removeItem(key));
                console.log(`🗑️ Cleared ${notificationKeys.length} notification tracking entries`);
                toast.success(`Cleared tracking for ${notificationKeys.length} tasks - notifications will re-trigger`);
              }}
            >
              🗑️ Reset Notifications
            </CButton>
          </div>

          {/* AI Suggestion Buttons */}
          {aiSuggestions.length > 0 && (
            <CAlert color="success" className="mb-3">
              <h6>💡 Ask AI About Your Health</h6>
              <p className="mb-2 small mb-2">
                Click a question to get personalized insights based on your {rawObservations?.entry?.length || 0} observations
              </p>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px',
                justifyContent: 'flex-start'
              }}>
                {aiSuggestions.map((suggestion) => (
                  <CButton
                    key={suggestion.id}
                    color={suggestion.color}
                    size="sm"
                    variant="outline"
                    onClick={() => handleSuggestionClick(suggestion)}
                    style={{ 
                      borderRadius: '20px',
                      padding: '6px 14px',
                      fontSize: '13px',
                      whiteSpace: 'nowrap',
                      fontWeight: '500'
                    }}
                  >
                    {suggestion.icon} {suggestion.label}
                  </CButton>
                ))}
              </div>
            </CAlert>
          )}

          {/* Enhanced Health Insights */}
          {healthInsights.length > 0 && (
            <CAlert color="info" className="mb-3">
              <h6>🏥 Health Insights & Recommendations</h6>
              <CListGroup>
                {healthInsights.slice(0, 5).map((insight, idx) => (
                  <CListGroupItem 
                    key={idx}
                    color={insight.priority === 'high' ? 'warning' : insight.priority === 'medium' ? 'info' : 'light'}
                    className="d-flex flex-column"
                  >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <strong>{insight.message}</strong>
                        <br />
                        <small className="mb-2">
                          {insight.daysSince === Infinity ? 'No data found' : `${insight.daysSince} days since last entry`}
                        </small>
                      </div>
                      <CBadge color={insight.priority === 'high' ? 'danger' : insight.priority === 'medium' ? 'warning' : 'secondary'}>
                        {insight.category}
                      </CBadge>
                    </div>
                    {insight.suggestedAction && (
                      <div className="mt-2">
                        <small className="text-success">
                          💡 <strong>Suggestion:</strong> {insight.suggestedAction}
                        </small>
                      </div>
                    )}
                  </CListGroupItem>
                ))}
                {healthInsights.length > 5 && (
                  <CListGroupItem className="text-center mb-2">
                    ... and {healthInsights.length - 5} more insights
                  </CListGroupItem>
                )}
              </CListGroup>
            </CAlert>
          )}

          {/* Pending Tasks Summary */}
          {notificationsEnabled && tasks.length > 0 && (
            <div>
              <h6>Pending Tasks ({tasks.length})</h6>
              <CListGroup>
                {tasks.slice(0, 5).map((task, idx) => (
                  <CListGroupItem key={idx} className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{task.description}</strong>
                      <br />
                      <small className="mb-2">
                        Due: {new Date(task.dueTime).toLocaleString()}
                      </small>
                    </div>
                    <CBadge color={task.isUrgent ? 'danger' : 'primary'}>
                      {task.category || 'reminder'}
                    </CBadge>
                  </CListGroupItem>
                ))}
                {tasks.length > 5 && (
                  <CListGroupItem className="text-center mb-2">
                    ... and {tasks.length - 5} more tasks
                  </CListGroupItem>
                )}
              </CListGroup>
            </div>
          )}
        </CCardBody>
      </CCard>
      
      {/* AI Suggestion Response Modal */}
      <CModal
        visible={selectedSuggestion !== null}
        onClose={closeSuggestionModal}
        size="lg"
      >
        <CModalHeader closeButton>
          <CModalTitle>
            {selectedSuggestion?.icon} {selectedSuggestion?.label}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ color: '#1f2937' }}>
          <div className="disclaimer-box mb-3" style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '10px 15px', 
            borderRadius: '8px',
            borderLeft: '4px solid #17a2b8',
            color: '#4b5563'
          }}>
            <small style={{ color: '#4b5563' }}>
              <strong>💡 AI Insight</strong> - Based on your {selectedSuggestion?.context?.count || 'health'} observations.
              Always consult a healthcare professional for medical advice.
            </small>
          </div>
          
          {/* Data Summary */}
          {selectedSuggestion?.context && selectedSuggestion?.type !== 'overall' && (
            <div className="data-summary mb-3" style={{
              display: 'flex',
              gap: '15px',
              flexWrap: 'wrap',
              padding: '10px',
              backgroundColor: '#e9ecef',
              borderRadius: '8px'
            }}>
              <div>
                <small style={{ color: '#4b5563' }}>Average</small>
                <div><strong>{selectedSuggestion.context.avg} {selectedSuggestion.context.unit}</strong></div>
              </div>
              <div>
                <small style={{ color: '#4b5563' }}>Min</small>
                <div><strong>{selectedSuggestion.context.min}</strong></div>
              </div>
              <div>
                <small style={{ color: '#4b5563' }}>Max</small>
                <div><strong>{selectedSuggestion.context.max}</strong></div>
              </div>
              <div>
                <small style={{ color: '#4b5563' }}>Entries</small>
                <div><strong>{selectedSuggestion.context.count}</strong></div>
              </div>
            </div>
          )}
          
          {/* AI Response */}
          <div className="ai-response" style={{ 
            minHeight: '150px',
            padding: '15px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            {isSuggestionLoading ? (
              <div className="text-center p-4">
                <div className="spinner-border text-primary mb-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p style={{ color: '#4b5563' }}>Analyzing your health data...</p>
              </div>
            ) : suggestionResponse ? (
              <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                {suggestionResponse}
              </div>
            ) : (
              <div className="text-center" style={{ color: '#4b5563' }}>
                <p>Preparing analysis...</p>
              </div>
            )}
          </div>
        </CModalBody>
        <CModalFooter>
          {/* Audio Play/Stop Button */}
          <CButton
            color={isAudioPlaying ? 'danger' : 'success'}
            size="sm"
            onClick={isAudioPlaying ? stopAudio : playAudio}
            disabled={isSuggestionLoading || !suggestionResponse}
            style={{ marginRight: 'auto' }}
          >
            {isAudioPlaying ? '⏹️ Stop Audio' : '🔊 Play Audio'}
          </CButton>
          <CButton
            color="primary"
            size="sm"
            onClick={() => {
              stopAudio(); // Stop audio before regenerating
              handleSuggestionClick(selectedSuggestion);
            }}
            disabled={isSuggestionLoading}
          >
            🔄 Regenerate
          </CButton>
          <CButton
            color="secondary"
            onClick={closeSuggestionModal}
          >
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default CircadianAIAssistant;



